/**
 * A radix-2 fast Fourier transform, in place.
 *
 * Small and self-contained on purpose: the only thing in this project that needs
 * a spectrum is onset detection, and a dependency for three dozen lines of
 * arithmetic is a poor trade when the arithmetic can be checked directly against
 * a direct transform.
 *
 * The algorithm is the standard decimation-in-time Cooley-Tukey: reorder the
 * input by bit-reversed index, then combine adjacent pairs, then pairs of pairs,
 * and so on. Each stage is one pass over the array, so the whole thing is
 * n log n rather than the n squared a direct transform costs.
 */

/** True for 1, 2, 4, 8 ... — the only lengths this transform accepts. */
export function isPowerOfTwo(n: number): boolean {
  return n > 0 && (n & (n - 1)) === 0;
}

/** The next power of two at or above `n`. */
export function nextPowerOfTwo(n: number): number {
  let size = 1;
  while (size < n) size *= 2;
  return size;
}

/**
 * Transforms `re` and `im` in place. Both must be the same power-of-two length.
 *
 * Pass `inverse` to run it backwards; the scaling by 1/n is applied there, so a
 * forward transform followed by an inverse returns the original samples.
 */
export function fft(re: Float64Array, im: Float64Array, inverse = false): void {
  const n = re.length;
  if (n !== im.length) throw new Error('fft: real and imaginary parts must be the same length');
  if (!isPowerOfTwo(n)) throw new Error(`fft: length must be a power of two, got ${n}`);
  if (n === 1) return;

  // Bit-reversal permutation, done by carrying a reversed counter alongside the
  // forward one: cheaper than reversing each index from scratch.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i];
      re[i] = re[j];
      re[j] = tr;
      const ti = im[i];
      im[i] = im[j];
      im[j] = ti;
    }
  }

  const sign = inverse ? 1 : -1;
  for (let length = 2; length <= n; length <<= 1) {
    const angle = (sign * 2 * Math.PI) / length;
    const stepRe = Math.cos(angle);
    const stepIm = Math.sin(angle);
    for (let start = 0; start < n; start += length) {
      // The twiddle factor is stepped rather than recomputed per butterfly. Over
      // a stage of at most a few thousand steps the drift is far below what any
      // spectrum here resolves, and it removes two trigonometric calls per point.
      let wRe = 1;
      let wIm = 0;
      const half = length >> 1;
      for (let k = 0; k < half; k++) {
        const a = start + k;
        const b = a + half;
        const tr = re[b] * wRe - im[b] * wIm;
        const ti = re[b] * wIm + im[b] * wRe;
        re[b] = re[a] - tr;
        im[b] = im[a] - ti;
        re[a] += tr;
        im[a] += ti;
        const nextRe = wRe * stepRe - wIm * stepIm;
        wIm = wRe * stepIm + wIm * stepRe;
        wRe = nextRe;
      }
    }
  }

  if (inverse) {
    for (let i = 0; i < n; i++) {
      re[i] /= n;
      im[i] /= n;
    }
  }
}

/**
 * Magnitudes of the first half of the spectrum — the only half that carries
 * anything, for real input, since the rest is its mirror image.
 *
 * `frame` is copied, so the caller's samples are left alone.
 */
export function magnitudeSpectrum(frame: Float32Array | Float64Array, window?: Float64Array): Float64Array {
  const n = frame.length;
  if (!isPowerOfTwo(n)) throw new Error(`magnitudeSpectrum: length must be a power of two, got ${n}`);
  const re = new Float64Array(n);
  const im = new Float64Array(n);
  for (let i = 0; i < n; i++) re[i] = frame[i] * (window ? window[i] : 1);
  fft(re, im);

  const bins = n >> 1;
  const out = new Float64Array(bins);
  for (let k = 0; k < bins; k++) out[k] = Math.hypot(re[k], im[k]);
  return out;
}

/** A periodic Hann window, the usual choice for analysis frames. */
export function hannWindow(length: number): Float64Array {
  const w = new Float64Array(length);
  for (let i = 0; i < length; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / length);
  return w;
}
